/**
 * Vocabulario Técnica Sistemática de Cata WSET® Nivel 3 (3W-Version 2.0, 2016).
 * Copyright Wine & Spirit Education Trust 2016.
 *
 * Las escalas con "media(-)" y "media(+)" reflejan exactamente la nomenclatura
 * oficial. Los valores internos usan underscore para ser URL/JSON-safe.
 */

// ========================== ASPECTO ==========================

export const CLARIDAD = [
  { value: 'claro', label: 'Claro' },
  { value: 'turbio', label: 'Turbio (¿defectuoso?)' },
];

export const INTENSIDAD_ASPECTO = [
  { value: 'palida', label: 'Pálida' },
  { value: 'media', label: 'Media' },
  { value: 'profunda', label: 'Profunda' },
];

export const COLOR_BLANCO = [
  { value: 'verde_limon', label: 'Verde limón' },
  { value: 'amarillo_limon', label: 'Amarillo limón' },
  { value: 'dorado', label: 'Dorado' },
  { value: 'ambar', label: 'Ámbar' },
  // Diferenciado del marrón del tinto: blancos muy oxidados (Madeira viejo, jerez).
  { value: 'marron_blanco', label: 'Marrón' },
];

export const COLOR_ROSADO = [
  { value: 'rosado', label: 'Rosado' },
  { value: 'salmon', label: 'Salmón' },
  { value: 'naranja', label: 'Naranja' },
];

export const COLOR_TINTO = [
  { value: 'purpura', label: 'Púrpura' },
  { value: 'rubi', label: 'Rubí' },
  { value: 'granate', label: 'Granate' },
  { value: 'teja', label: 'Teja' },
  { value: 'marron', label: 'Marrón' },
];

// ========================== NARIZ ==========================

export const CONDICION_NARIZ = [
  { value: 'limpia', label: 'Limpia' },
  { value: 'no_limpia', label: 'No limpia (¿defectuosa?)' },
];

/** Escala WSET de 5 niveles con media(-) y media(+). Usada en intensidad nariz,
 *  intensidad sabor, final. */
export const ESCALA_5 = [
  { value: 'ligera', label: 'Ligera' },
  { value: 'media_menos', label: 'Media(-)' },
  { value: 'media', label: 'Media' },
  { value: 'media_mas', label: 'Media(+)' },
  { value: 'pronunciada', label: 'Pronunciada' },
];

export const EVOLUCION = [
  { value: 'joven', label: 'Joven' },
  { value: 'en_evolucion', label: 'En evolución' },
  { value: 'evolucionado', label: 'Evolucionado' },
  { value: 'cansado', label: 'Cansado / Pasó su mejor momento' },
];

// ========================== BOCA ==========================

export const DULZOR = [
  { value: 'seco', label: 'Seco' },
  { value: 'casi_seco', label: 'Casi seco' },
  { value: 'semiseco', label: 'Semiseco' },
  { value: 'semidulce', label: 'Semidulce' },
  { value: 'dulce', label: 'Dulce' },
  { value: 'muy_dulce', label: 'Muy dulce' },
];

export const ACIDEZ = [
  { value: 'baja', label: 'Baja' },
  { value: 'media_menos', label: 'Media(-)' },
  { value: 'media', label: 'Media' },
  { value: 'media_mas', label: 'Media(+)' },
  { value: 'alta', label: 'Alta' },
];

export const TANINO = [
  { value: 'bajo', label: 'Bajo' },
  { value: 'medio_menos', label: 'Medio(-)' },
  { value: 'medio', label: 'Medio' },
  { value: 'medio_mas', label: 'Medio(+)' },
  { value: 'alto', label: 'Alto' },
];

export const ALCOHOL = [
  { value: 'bajo', label: 'Bajo' },
  { value: 'medio', label: 'Medio' },
  { value: 'alto', label: 'Alto' },
];

export const CUERPO = [
  { value: 'poco', label: 'Poco' },
  { value: 'medio_menos', label: 'Medio(-)' },
  { value: 'medio', label: 'Medio' },
  { value: 'medio_mas', label: 'Medio(+)' },
  { value: 'mucho', label: 'Mucho' },
];

export const BURBUJA = [
  { value: 'delicada', label: 'Delicada' },
  { value: 'cremosa', label: 'Cremosa' },
  { value: 'agresiva', label: 'Agresiva' },
];

/** Final / persistencia. Usa medio(-) y medio(+). */
export const FINAL = [
  { value: 'corto', label: 'Corto' },
  { value: 'medio_menos', label: 'Medio(-)' },
  { value: 'medio', label: 'Medio' },
  { value: 'medio_mas', label: 'Medio(+)' },
  { value: 'largo', label: 'Largo' },
];

// ========================== CONCLUSIONES ==========================

export const NIVEL_CALIDAD = [
  { value: 'defectuoso', label: 'Defectuoso' },
  { value: 'pobre', label: 'Pobre' },
  { value: 'aceptable', label: 'Aceptable' },
  { value: 'bueno', label: 'Bueno' },
  { value: 'muy_bueno', label: 'Muy bueno' },
  { value: 'excelente', label: 'Excelente' },
];

export const POTENCIAL_GUARDA = [
  { value: 'demasiado_joven', label: 'Demasiado joven' },
  {
    value: 'beber_ahora_con_potencial',
    label: 'Se puede beber ahora, pero tiene potencial para el envejecimiento',
  },
  {
    value: 'beber_ahora_no_envejece',
    label: 'Beber ahora: no adecuado para el envejecimiento o para un mayor envejecimiento',
  },
  { value: 'demasiado_viejo', label: 'Demasiado viejo' },
];

// ========================== LÉXICO AROMÁTICO ==========================

export interface AromaGroup {
  value: string;
  label: string;
  /** Descriptores del léxico oficial WSET 3 — usar como hint en el placeholder/tooltip. */
  hint: string;
}

export const AROMAS_PRIMARIOS: AromaGroup[] = [
  { value: 'floral', label: 'Floral', hint: 'acacia, madreselva, manzanilla, flor de saúco, geranio, árbol en flor, rosa, violeta' },
  { value: 'fruta_verde', label: 'Fruta verde', hint: 'manzana, grosella espinosa, pera, pear drop, membrillo, uva' },
  { value: 'fruta_citrica', label: 'Fruta cítrica', hint: 'pomelo, limón, lima, cáscara de naranja, cáscara de limón' },
  { value: 'fruta_hueso', label: 'Fruta de hueso', hint: 'melocotón, albaricoque, nectarina' },
  { value: 'fruta_tropical', label: 'Fruta tropical', hint: 'plátano, lichi, mango, melón, fruta de la pasión, piña' },
  { value: 'fruta_roja', label: 'Fruta roja', hint: 'grosella roja, arándano rojo, frambuesa, fresa, cereza roja, ciruela roja' },
  { value: 'fruta_negra', label: 'Fruta negra', hint: 'grosella negra, zarzamora, zarza, arándano azul, cereza negra, ciruela negra' },
  { value: 'fruta_seca_cocida', label: 'Fruta seca/cocida', hint: 'higo, ciruela pasa, uva pasa, sultana, kirsch, mermelada, frutas al horno' },
  { value: 'herbaceo', label: 'Herbáceo', hint: 'pimiento verde, hierba, hoja de tomate, espárrago, hoja de grosella negra' },
  { value: 'herbario', label: 'Herbario', hint: 'eucalipto, menta, medicinal, lavanda, hinojo, eneldo' },
  { value: 'especias_picantes', label: 'Especias picantes', hint: 'pimienta negra/blanca, regaliz' },
  { value: 'otros_primarios', label: 'Otros', hint: 'sílex, piedras mojadas, lana mojada' },
];

export const AROMAS_SECUNDARIOS: AromaGroup[] = [
  { value: 'levaduras', label: 'Levaduras (lías, autólisis)', hint: 'galleta, pan, pan tostado, pastelería, brioche, masa de pan, queso' },
  { value: 'fml', label: 'Fermentación maloláctica', hint: 'mantequilla, queso, nata' },
  { value: 'roble', label: 'Roble', hint: 'vainilla, clavo, nuez moscada, coco, butterscotch, pan tostado, cedro, madera tostada, humo, chocolate, café, resinoso' },
];

export const AROMAS_TERCIARIOS: AromaGroup[] = [
  { value: 'oxidacion', label: 'Oxidación deliberada', hint: 'almendra, mazapán, avellana, nuez, chocolate, café, toffee, caramelo' },
  { value: 'evolucion_blanco', label: 'Evolución de la fruta (blanco)', hint: 'albaricoque seco, mermelada de cítricos, manzana seca, plátano seco' },
  { value: 'evolucion_tinto', label: 'Evolución de la fruta (tinto)', hint: 'higo, ciruela pasa, alquitrán, zarzamora seca, arándano rojo seco, zarzamora cocida' },
  { value: 'envejec_botella_blanco', label: 'Envejecimiento en botella (blanco)', hint: 'petróleo, queroseno, canela, jengibre, nuez moscada, pan tostado, frutos secos, champiñón, heno, miel' },
  { value: 'envejec_botella_tinto', label: 'Envejecimiento en botella (tinto)', hint: 'cuero, piso forestal, tierra, champiñón, caza, tabaco, vegetal, hojas mojadas, cárnico, corral' },
];

// ========================== TYPES ==========================

export interface VistaData {
  claridad?: string;
  intensidad?: string;
  color?: string;
  observaciones?: string;
}

export interface NarizData {
  condicion?: string;
  intensidad?: string;
  evolucion?: string;
  aromas_primarios?: string[];
  aromas_secundarios?: string[];
  aromas_terciarios?: string[];
  caracteristicas?: string;
}

export interface BocaData {
  dulzor?: string;
  acidez?: string;
  tanino?: string;
  alcohol?: string;
  cuerpo?: string;
  burbuja?: string;
  intensidad_sabor?: string;
  final?: string;
  caracteristicas?: string;
}

export interface ConclusionData {
  nivel_calidad?: string;
  potencial_guarda?: string;
}

export interface TastingNote {
  id: number;
  wine_id: number;
  user_id: number;
  tasted_on: string;
  vista: VistaData | null;
  nariz: NarizData | null;
  boca: BocaData | null;
  conclusion: string | null;
  conclusion_data: ConclusionData | null;
  score: number | null;
  scale: '100' | '20' | 'propio';
  is_public: boolean;
  wine?: { id: number; name: string; vintage: number | null; format: string };
  user_name?: string;
  created_at: string;
  updated_at: string;
}
